import { FormEvent, useState } from 'react';
import { changeEmailPassword, getAuthErrorMessage } from '../../auth/auth.api';
import { TELEGRAM_LINKS, openExternalLink } from '../../../shared/links';

type AccountPageProps = {
  canConnectTelegram: boolean;
  isTelegramLinked: boolean;
  isTelegramConnecting: boolean;
  telegramStatus: string;
  telegramUsername: string;
  userEmail?: string;
  onConnectTelegram: () => void;
  onLogout: () => void;
};

export function AccountPage({
  canConnectTelegram,
  isTelegramLinked,
  isTelegramConnecting,
  telegramStatus,
  telegramUsername,
  userEmail,
  onConnectTelegram,
  onLogout,
}: AccountPageProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isPasswordChanging, setIsPasswordChanging] = useState(false);
  const telegramName = telegramUsername ? `@${telegramUsername.replace(/^@/, '')}` : 'Подключен';
  const profileHint = isTelegramLinked && telegramUsername ? telegramName : 'Через эту почту вы входите на сайт и управляете подпиской.';

  const handlePasswordChange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isPasswordChanging) {
      return;
    }

    setPasswordStatus('');
    setPasswordError('');

    if (!currentPassword) {
      setPasswordError('Введите текущий пароль');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Новый пароль должен быть не короче 8 символов');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError('Новый пароль должен отличаться от текущего');
      return;
    }

    setIsPasswordChanging(true);

    try {
      await changeEmailPassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setPasswordStatus('Пароль изменен');
    } catch (error) {
      setPasswordError(getAuthErrorMessage(error));
    } finally {
      setIsPasswordChanging(false);
    }
  };

  return (
    <div className="cabinet-page">
      <h1>Личный кабинет</h1>
      <div className="account-grid">
        <section className="cabinet-panel account-profile-card">
          <span className="account-avatar" aria-hidden="true">◎</span>
          <div>
            <span className="cabinet-muted">Вход по почте</span>
            <h2>{userEmail || 'Email не указан'}</h2>
            <p>{profileHint}</p>
          </div>
        </section>

        <section className="cabinet-panel account-links-card">
          <h2>Связанные аккаунты</h2>
          <div className="account-link-row">
            <span>Email</span>
            <strong className="green-text">Подключен</strong>
          </div>
          <div className="account-link-row">
            <span>Telegram</span>
            <strong className={isTelegramLinked ? 'green-text' : canConnectTelegram ? 'amber-text' : 'cabinet-muted'}>
              {isTelegramLinked ? 'Подключен' : canConnectTelegram ? 'Можно связать' : 'Откройте из Telegram'}
            </strong>
          </div>
          {!isTelegramLinked && canConnectTelegram ? (
            <button className="button button-primary" type="button" onClick={onConnectTelegram}>
              {isTelegramConnecting ? 'Связываем...' : 'Связать Telegram'}
            </button>
          ) : null}
          {telegramStatus ? <p className="account-status">{telegramStatus}</p> : null}
        </section>

        {isTelegramLinked ? (
          <section className="cabinet-panel account-telegram-card">
            <div>
              <span className="cabinet-muted">Telegram</span>
              <h2>{telegramName}</h2>
              <p>Ваш Telegram уже связан с кабинетом. Mini App остается доступен для быстрых действий и уведомлений.</p>
            </div>
            <button className="telegram-miniapp-link" type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.miniApp)}>
              Открыть MifVPN в Telegram
            </button>
          </section>
        ) : (
          <section className="cabinet-panel account-flow-card">
            <h2>Как перенести подписку из Telegram</h2>
            <div className="account-flow-step">
              <span>1</span>
              <p>Войдите в Telegram-бот MifVPN на аккаунте, где уже есть подписка.</p>
              <button className="account-flow-action" type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.bot)}>
                Открыть
              </button>
            </div>
            <div className="account-flow-step">
              <span>2</span>
              <p>Перейдите на сайте в личный кабинет и нажмите «Связать Telegram».</p>
            </div>
            <div className="account-flow-step">
              <span>3</span>
              <p>Повторно авторизуйтесь на сайте, если система попросит.</p>
            </div>
            <div className="account-flow-step">
              <span>4</span>
              <p>После привязки подписка, баланс и бонусы появятся на сайте автоматически.</p>
            </div>
          </section>
        )}

        <section className="cabinet-panel account-actions-card">
          <h2>Быстрые действия</h2>
          <button type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.bot)}>Открыть бота</button>
          <button type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.support)}>Поддержка</button>
          <button type="button" onClick={onLogout}>Выйти</button>
        </section>

        <section className="cabinet-panel account-password-card">
          <h2>Поменять пароль</h2>
          <form className="account-password-form" onSubmit={handlePasswordChange} noValidate>
            <label>
              <span>Текущий пароль</span>
              <input
                autoComplete="current-password"
                minLength={8}
                placeholder="Введите текущий пароль"
                required
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </label>
            <label>
              <span>Новый пароль</span>
              <input
                autoComplete="new-password"
                minLength={8}
                placeholder="Минимум 8 символов"
                required
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </label>
            {passwordError ? <p className="account-password-message error">{passwordError}</p> : null}
            {passwordStatus ? <p className="account-password-message success">{passwordStatus}</p> : null}
            <button className="button button-primary" type="submit" disabled={isPasswordChanging}>
              {isPasswordChanging ? 'Меняем...' : 'Изменить пароль'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
